import { useEffect, useRef } from 'react'

import { color, markStyles, radius } from '../../theme/tokens'
import type { Turn } from '../../lib/types'

/**
 * Transcrição ao vivo.
 *
 * Faz auto-scroll enquanto o operador está no fim da lista, mas para de forçar
 * assim que ele rola para cima — quem está lendo um turno anterior durante a
 * chamada não pode ser arrastado de volta.
 */
export function Transcript({ turns, clientName }: { turns: Turn[]; clientName: string }) {
  const scroller = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)

  useEffect(() => {
    const element = scroller.current
    if (element && pinned.current) element.scrollTop = element.scrollHeight
  }, [turns])

  const onScroll = () => {
    const element = scroller.current
    if (!element) return
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight
    pinned.current = distance < 48
  }

  return (
    <div
      ref={scroller}
      onScroll={onScroll}
      className="dk"
      role="log"
      aria-live="polite"
      aria-label="Transcrição da chamada"
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {turns.map((turn, index) => {
        const isAi = turn.speaker === 'ai'
        return (
          <div
            key={index}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              animation: 'bub .28s ease both',
              alignItems: isAi ? 'flex-start' : 'flex-end',
            }}
          >
            <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: '.05em',
                  color: isAi ? color.brandLight : color.panelTextMuted,
                }}
              >
                <span>{isAi ? 'ALICE · IA' : clientName.toUpperCase()}</span>
                <span
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    color: '#6f7d95',
                    fontWeight: 500,
                  }}
                >
                  {turn.at}
                </span>
              </div>
              <div
                style={{
                  borderRadius: radius.xl,
                  padding: '11px 14px',
                  fontSize: 14.5,
                  lineHeight: 1.5,
                  textWrap: 'pretty',
                  background: isAi ? color.brandTint : color.surface,
                  color: color.ink,
                  border: `1px solid ${isAi ? color.brandBorder : color.border}`,
                  borderLeft: `3px solid ${isAi ? color.brand : '#c8cfda'}`,
                }}
              >
                {turn.text}
              </div>
            </div>

            {turn.marks.map((mark, markIndex) => {
              const style = markStyles[mark.kind]
              return (
                <div
                  key={markIndex}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    fontSize: 11.5,
                    fontWeight: 500,
                    padding: '4px 10px',
                    borderRadius: radius.pill,
                    background: style.bg,
                    color: style.fg,
                    border: `1px solid ${style.bd}`,
                  }}
                >
                  <span style={{ fontSize: 12 }} aria-hidden>
                    {style.icon}
                  </span>
                  {mark.label}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
